import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, X, Check, Loader2, Clock, Trash2, CalendarIcon, Layers, Image } from "lucide-react";
import { format } from "date-fns";
import BackButton from "@/components/BackButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOURS = Array.from({ length: 24 }, (_, i) => {
  const displayHour = i + 1;
  const storageHour = displayHour === 24 ? 0 : displayHour;
  return {
    value: String(displayHour),
    storageHour,
    displayHour,
    label: `${String(displayHour).padStart(2, "0")}:00`,
    range: `${String(displayHour).padStart(2, "0")}:00 - ${String(displayHour).padStart(2, "0")}:59`,
  };
});

export default function InfoUploadPage() {
  const navigate = useNavigate();
  const singleFileRef = useRef(null);
  const bulkFileRef = useRef(null);

  const [date, setDate] = useState(new Date());
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("bulk");

  // Single upload state
  const [hour, setHour] = useState("1");
  const [singleImage, setSingleImage] = useState(null);
  const [singlePreview, setSinglePreview] = useState(null);
  const [singleUploading, setSingleUploading] = useState(false);
  const [singleStatus, setSingleStatus] = useState(null);

  // Bulk upload state
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkDragActive, setBulkDragActive] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadStatus, setBulkUploadStatus] = useState({});

  // Existing images
  const [existingImages, setExistingImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(true);

  useEffect(() => {
    fetchExistingImages();
  }, [date]);

  const fetchExistingImages = async () => {
    setLoadingImages(true);
    try {
      const y = date.getFullYear();
      const m = date.getMonth() + 1;
      const d = date.getDate();
      const res = await fetch(`${API}/info/list?year=${y}&month=${m}&day=${d}`);
      const data = await res.json();
      if (data.success) {
        setExistingImages(data.images || []);
      }
    } catch (err) {
      console.error("Failed to fetch info images:", err);
    } finally {
      setLoadingImages(false);
    }
  };

  // ── Single Upload ──
  const handleSingleFileSelect = (file) => {
    if (file && file.type.startsWith("image/")) {
      setSingleImage(file);
      setSinglePreview(URL.createObjectURL(file));
      setSingleStatus(null);
    }
  };

  const handleSingleUpload = async () => {
    if (!singleImage) return;
    setSingleUploading(true);
    setSingleStatus(null);

    const formData = new FormData();
    formData.append("file", singleImage);
    formData.append("year", String(date.getFullYear()));
    formData.append("month", String(date.getMonth() + 1));
    formData.append("day", String(date.getDate()));
    formData.append("hour", hour);

    try {
      const res = await fetch(`${API}/info/upload`, { method: "POST", body: formData });
      const data = await res.json();
      if (data.success) {
        const dateStr = format(date, "MMM d, yyyy");
        setSingleStatus({ type: "success", message: `Uploaded for ${dateStr} ${hour.padStart(2, "0")}:00` });
        fetchExistingImages();
        clearSingleImage();
      } else {
        setSingleStatus({ type: "error", message: data.error || "Upload failed" });
      }
    } catch {
      setSingleStatus({ type: "error", message: "Network error" });
    } finally {
      setSingleUploading(false);
    }
  };

  const clearSingleImage = () => {
    if (singlePreview) URL.revokeObjectURL(singlePreview);
    setSingleImage(null);
    setSinglePreview(null);
    if (singleFileRef.current) singleFileRef.current.value = "";
  };

  // ── Bulk Upload ──
  const assignBulkFiles = (selectedFiles) => {
    const imageFiles = Array.from(selectedFiles).filter(f => f.type.startsWith("image/"));
    const newFiles = imageFiles.slice(0, 24).map((file, index) => ({
      file,
      hour: HOURS[index]?.storageHour,
      displayHour: HOURS[index]?.displayHour,
      preview: URL.createObjectURL(file),
    }));
    setBulkFiles(newFiles);
    setBulkUploadStatus({});
  };

  const clearBulkFiles = () => {
    bulkFiles.forEach(f => URL.revokeObjectURL(f.preview));
    setBulkFiles([]);
    setBulkUploadStatus({});
    if (bulkFileRef.current) bulkFileRef.current.value = "";
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;
    setBulkUploading(true);
    setBulkUploadStatus({});

    for (const fileData of bulkFiles) {
      const { file, displayHour } = fileData;
      const key = displayHour;
      try {
        setBulkUploadStatus(prev => ({ ...prev, [key]: "uploading" }));

        const formData = new FormData();
        formData.append("file", file);
        formData.append("year", String(date.getFullYear()));
        formData.append("month", String(date.getMonth() + 1));
        formData.append("day", String(date.getDate()));
        formData.append("hour", String(displayHour));

        const res = await fetch(`${API}/info/upload`, { method: "POST", body: formData });
        const data = await res.json();
        setBulkUploadStatus(prev => ({ ...prev, [key]: data.success ? "success" : "error" }));
      } catch {
        setBulkUploadStatus(prev => ({ ...prev, [key]: "error" }));
      }
    }

    setBulkUploading(false);
    fetchExistingImages();
  };

  const bulkSuccessCount = Object.values(bulkUploadStatus).filter(s => s === "success").length;

  // ── Delete ──
  const handleDelete = async (img) => {
    const label = `${format(date, "MMM d, yyyy")} ${String(img.display_hour).padStart(2, "0")}:00`;
    if (!window.confirm(`Delete info image for ${label}?`)) return;
    try {
      const res = await fetch(
        `${API}/info/delete?year=${img.year}&month=${img.month}&day=${img.day}&hour=${img.display_hour}`,
        { method: "DELETE" }
      );
      const data = await res.json();
      if (data.success) fetchExistingImages();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  return (
    <div className="upload-page">
      <div className="upload-bg-pattern" />
      <div className="upload-container">
        <BackButton />

        <div className="upload-header">
          <h1 className="upload-title">Upload Important Info</h1>
          <p className="upload-subtitle">
            Upload info images that display below charts for a specific date and hour
          </p>
        </div>

        {/* Date Picker */}
        <div className="bulk-date-picker" style={{ marginBottom: "1rem" }}>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="bulk-date-btn" data-testid="info-date-picker">
                <CalendarIcon size={18} />
                {format(date, "MMMM d, yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="calendar-popover" align="center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => { if (d) { setDate(d); setCalendarOpen(false); } }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Upload Mode Selector */}
        <div className="bulk-upload-tabs">
          <div className="bulk-tabs-list">
            <button
              className={`bulk-tab ${activeTab === 'bulk' ? 'active' : ''}`}
              data-state={activeTab === 'bulk' ? 'active' : 'inactive'}
              onClick={() => setActiveTab('bulk')}
              data-testid="bulk-info-tab"
            >
              <Layers size={16} />
              Bulk Upload
            </button>
            <button
              className={`bulk-tab ${activeTab === 'single' ? 'active' : ''}`}
              data-state={activeTab === 'single' ? 'active' : 'inactive'}
              onClick={() => setActiveTab('single')}
              data-testid="single-info-tab"
            >
              <Image size={16} />
              Single Upload
            </button>
          </div>

          {activeTab === 'bulk' && (
            <div className="mt-2">
            {bulkFiles.length === 0 ? (
              <div
                className={`bulk-dropzone ${bulkDragActive ? "drag-active" : ""}`}
                onDragEnter={(e) => { e.preventDefault(); setBulkDragActive(true); }}
                onDragLeave={(e) => { e.preventDefault(); setBulkDragActive(false); }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); setBulkDragActive(false); assignBulkFiles(e.dataTransfer.files); }}
                onClick={() => bulkFileRef.current?.click()}
                data-testid="info-bulk-dropzone"
              >
                <Upload size={48} className="dropzone-icon" />
                <p className="dropzone-text">
                  Drag & drop up to 24 info images here, or click to select
                </p>
                <p className="dropzone-hint">
                  Images for {format(date, "MMM d, yyyy")} — assigned to timeslots in order (01:00, 02:00, ...)
                </p>
                <input
                  ref={bulkFileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => assignBulkFiles(e.target.files)}
                  className="hidden-input"
                  data-testid="info-bulk-file-input"
                />
              </div>
            ) : (
              <div className="bulk-files-container">
                <div className="bulk-files-header">
                  <span className="bulk-files-count">
                    {bulkFiles.length} info image{bulkFiles.length !== 1 ? "s" : ""} selected
                  </span>
                  <Button variant="ghost" size="sm" onClick={clearBulkFiles} disabled={bulkUploading} data-testid="clear-bulk-info-btn">
                    <X size={16} /> Clear All
                  </Button>
                </div>
                <div className="bulk-preview-grid">
                  {bulkFiles.map((fd, index) => (
                    <div key={index} className={`bulk-preview-item ${bulkUploadStatus[fd.displayHour] || ""}`} data-testid={`info-preview-${fd.displayHour}`}>
                      <div className="preview-header">
                        <span className="preview-time">{HOURS[index]?.label}</span>
                        {bulkUploadStatus[fd.displayHour] === "success" && <Check size={14} className="status-icon success" />}
                        {bulkUploadStatus[fd.displayHour] === "uploading" && <Loader2 size={14} className="status-icon uploading animate-spin" />}
                        {bulkUploadStatus[fd.displayHour] === "error" && <X size={14} className="status-icon error" />}
                      </div>
                      <div className="preview-image-wrap">
                        <img src={fd.preview} alt={`Info ${HOURS[index]?.label}`} />
                      </div>
                      <span className="preview-filename">{fd.file.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="bulk-upload-actions">
              <Button
                className="upload-submit-btn"
                onClick={handleBulkUpload}
                disabled={bulkFiles.length === 0 || bulkUploading}
                data-testid="info-bulk-upload-btn"
              >
                {bulkUploading ? (
                  <><Loader2 size={18} className="animate-spin" /> Uploading...</>
                ) : (
                  <><Upload size={18} /> Upload {bulkFiles.length} Info Image{bulkFiles.length !== 1 ? "s" : ""}</>
                )}
              </Button>
            </div>
            {bulkSuccessCount > 0 && (
              <div className="bulk-upload-success">
                <p className="success-message">{bulkSuccessCount} info image{bulkSuccessCount !== 1 ? "s" : ""} uploaded successfully!</p>
              </div>
            )}
            </div>
          )}

          {activeTab === 'single' && (
            <div className="mt-2">
            <div className="upload-datetime">
              <div className="time-selector">
                <label><Clock size={16} /> Hour Slot</label>
                <Select value={hour} onValueChange={setHour}>
                  <SelectTrigger data-testid="hour-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS.map((h) => (
                      <SelectItem key={h.value} value={h.value}>
                        {h.label} ({h.range})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div
              className={`upload-dropzone ${singlePreview ? "has-image" : ""}`}
              onClick={() => !singlePreview && singleFileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleSingleFileSelect(e.dataTransfer.files[0]); }}
            >
              <input ref={singleFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleSingleFileSelect(e.target.files[0])} data-testid="info-single-file-input" />
              {singlePreview ? (
                <div className="upload-preview">
                  <img src={singlePreview} alt="Preview" />
                  <Button variant="ghost" className="clear-image-btn" onClick={(e) => { e.stopPropagation(); clearSingleImage(); setSingleStatus(null); }}>
                    <X size={20} />
                  </Button>
                </div>
              ) : (
                <div className="upload-placeholder">
                  <Upload size={48} />
                  <p>Click or drag to upload info image</p>
                  <p className="upload-hint">PNG, JPG up to 10MB</p>
                </div>
              )}
            </div>
            {singleImage && (
              <Button className="upload-submit-btn" onClick={handleSingleUpload} disabled={singleUploading} data-testid="info-single-upload-btn">
                {singleUploading ? (
                  <><Loader2 className="animate-spin" size={18} /> Uploading...</>
                ) : (
                  <><Upload size={18} /> Upload Info Image</>
                )}
              </Button>
            )}
            {singleStatus && (
              <div className={`upload-status ${singleStatus.type}`} data-testid="single-upload-status">
                {singleStatus.type === "success" ? <Check size={18} /> : <X size={18} />}
                {singleStatus.message}
              </div>
            )}
            </div>
          )}
        </div>

        {/* Existing Images for Selected Date */}
        <div className="info-images-list">
          <h3>Info Images for {format(date, "MMMM d, yyyy")}</h3>
          {loadingImages ? (
            <p>Loading...</p>
          ) : existingImages.length === 0 ? (
            <p className="no-images">No info images uploaded for this date</p>
          ) : (
            <div className="info-images-grid">
              {existingImages
                .sort((a, b) => a.display_hour - b.display_hour)
                .map((img) => (
                  <div key={img.date_key} className="info-image-card" data-testid={`info-card-${img.date_key}`}>
                    <div className="info-image-preview">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/info_images/${img.filename}`}
                        alt={`Info ${img.display_hour}:00`}
                      />
                    </div>
                    <div className="info-image-label">
                      <Clock size={14} />
                      {String(img.display_hour).padStart(2, "0")}:00
                    </div>
                    <Button variant="ghost" className="info-delete-btn" onClick={() => handleDelete(img)} data-testid={`info-delete-${img.date_key}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
            </div>
          )}
        </div>

        <footer className="page-footer">
          <p>Qi Men Dun Jia Plotter</p>
          <p className="footer-copyright">
            Copyright © 2026 Steven Yong - Digital Dive Coaching Sdn. Bhd. All rights reserved worldwide.
          </p>
        </footer>
      </div>
    </div>
  );
}
